import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, ArrowUpRight } from "lucide-react";
import { api, formatINR, formatApiErrorDetail } from "@/lib/api";
import { imageForFormat } from "@/lib/images";
import SmartImage from "@/components/common/SmartImage";

export default function AdvertiserShortlist() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/advertiser/shortlist");
      setRows(data);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function remove(invId) {
    try {
      await api.delete(`/advertiser/shortlist/${invId}`);
      toast.success("Removed from shortlist");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  return (
    <div data-testid="advertiser-shortlist">
      <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)]">Shortlist</div>
      <h1 className="mt-3 font-serif-display text-4xl text-[var(--ink)]">Your saved ad spaces · {rows.length}</h1>
      <p className="mt-2 text-[var(--slate-1)]">Curate, compare, and convert to a campaign proposal.</p>

      {loading ? <div className="mt-10 text-[var(--slate-2)]">Loading…</div> : rows.length === 0 ? (
        <div className="mt-10 surface p-12 text-center" data-testid="shortlist-empty">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--gold)]">Empty</div>
          <div className="mt-3 font-serif text-3xl">No shortlisted placements yet.</div>
          <p className="mt-2 text-[var(--slate-1)]">Browse the marketplace and tap the heart to save spaces here.</p>
          <Link to="/marketplace" className="mt-6 inline-flex btn-primary">Browse marketplace</Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {rows.map((r) => {
            const item = r.item;
            return (
              <div key={r.id} className="surface surface-hover" data-testid={`sl-row-${item.id}`}>
                <Link to={`/inventory/${item.id}`} className="block">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <SmartImage src={item.photos?.[0]} format={item.format} alt={item.format} className="absolute inset-0 w-full h-full object-cover img-luxe" />
                    <div className="absolute inset-0 hero-overlay" />
                    <div className="absolute top-3 left-3 chip bg-white/95">{item.format_category}</div>
                  </div>
                </Link>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-serif text-lg text-[var(--ink)] truncate">{item.format}</div>
                      <div className="text-xs text-[var(--slate-2)] truncate">{item.mall_name} · {item.city}</div>
                    </div>
                    <button onClick={() => remove(item.id)} className="text-[var(--slate-2)] hover:text-red-600" aria-label="Remove" data-testid={`sl-remove-${item.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm font-medium text-[var(--ink)]">
                      {formatINR(item.price_per_day)}<span className="text-[var(--slate-2)] font-normal text-xs"> / day</span>
                    </div>
                    <Link to={`/inventory/${item.id}`} className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[var(--gold)] hover:text-[var(--ink)] inline-flex items-center gap-1">
                      View <ArrowUpRight size={11} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
